import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Assessment,
  Download,
  Email,
  Schedule,
} from '@mui/icons-material';
import { User } from '../types';

interface ReportGeneratorProps {
  user: User;
}

const ReportGenerator: React.FC<ReportGeneratorProps> = ({ user }) => {
  const [reportType, setReportType] = useState('health');
  const [assetId, setAssetId] = useState('');
  const [componentId, setComponentId] = useState('');
  const [timeRange, setTimeRange] = useState('7');
  const [format, setFormat] = useState('pdf');
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const reportTypes = [
    { value: 'health', label: 'Health Report' },
    { value: 'predictive_maintenance', label: 'Predictive Maintenance' },
    { value: 'anomaly', label: 'Anomaly Report' },
    { value: 'performance', label: 'Performance Report' },
    { value: 'inspection', label: 'Inspection Report' },
    { value: 'compliance', label: 'Compliance Report' },
    { value: 'educational', label: 'Educational Report' },
  ];

  const timeRanges = [
    { value: '1', label: 'Last 24 hours' },
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
  ];

  const formats = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' },
    { value: 'json', label: 'JSON' },
  ];

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      // Mock report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      setGenerated(true);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Report Generator
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Generate comprehensive reports for your assets and components
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Report Configuration
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Report Type</InputLabel>
                    <Select
                      value={reportType}
                      label="Report Type"
                      onChange={(e) => setReportType(e.target.value)}
                    >
                      {reportTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Time Range</InputLabel>
                    <Select
                      value={timeRange}
                      label="Time Range"
                      onChange={(e) => setTimeRange(e.target.value)}
                    >
                      {timeRanges.map((range) => (
                        <MenuItem key={range.value} value={range.value}>
                          {range.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Asset ID (Optional)"
                    value={assetId}
                    onChange={(e) => setAssetId(e.target.value)}
                    helperText="Leave empty for all assets"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Component ID (Optional)"
                    value={componentId}
                    onChange={(e) => setComponentId(e.target.value)}
                    helperText="Leave empty for all components"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Format</InputLabel>
                    <Select
                      value={format}
                      label="Format"
                      onChange={(e) => setFormat(e.target.value)}
                    >
                      {formats.map((fmt) => (
                        <MenuItem key={fmt.value} value={fmt.value}>
                          {fmt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Assessment />}
                  onClick={handleGenerateReport}
                  disabled={loading}
                >
                  {loading ? 'Generating...' : 'Generate Report'}
                </Button>
                
                {generated && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                    >
                      Download
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Email />}
                    >
                      Email
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Schedule />}
                    >
                      Schedule
                    </Button>
                  </>
                )}
              </Box>

              {loading && (
                <Box sx={{ mt: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                    Generating report...
                  </Typography>
                </Box>
              )}

              {generated && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Report generated successfully! You can now download or email it.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Report Types
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reportTypes.map((type) => (
                  <Box
                    key={type.value}
                    sx={{
                      p: 2,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      cursor: 'pointer',
                      backgroundColor: reportType === type.value ? 'primary.light' : 'transparent',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={() => setReportType(type.value)}
                  >
                    <Typography variant="subtitle2">{type.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {type.value === 'health' && 'Component health analysis and trends'}
                      {type.value === 'predictive_maintenance' && 'Maintenance predictions and recommendations'}
                      {type.value === 'anomaly' && 'Detected anomalies and their analysis'}
                      {type.value === 'performance' && 'Performance metrics and KPIs'}
                      {type.value === 'inspection' && 'Inspection results and findings'}
                      {type.value === 'compliance' && 'Regulatory compliance status'}
                      {type.value === 'educational' && 'Educational content and experiments'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ReportGenerator;
